import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from 'recharts';
import { TrendingUp, TrendingDown, BarChart3, DollarSign } from 'lucide-react';

const growthData = [
  { month: 'Jan', totalClients: 8500, totalGroups: 650 },
  { month: 'Feb', totalClients: 9200, totalGroups: 680 },
  { month: 'Mar', totalClients: 10100, totalGroups: 720 },
  { month: 'Apr', totalClients: 10800, totalGroups: 760 },
  { month: 'May', totalClients: 11650, totalGroups: 810 },
  { month: 'Jun', totalClients: 12547, totalGroups: 847 }
];

const loanDistributionData = [
  { name: 'Regular Loans', value: 75, color: '#3b82f6' },
  { name: 'Advance Loans', value: 20, color: '#10b981' },
  { name: 'Emergency Loans', value: 5, color: '#ef4444' }
];

const collectionMethodsData = [
  { method: 'Cash', amount: 312450 },
  { method: 'Online', amount: 198300 },
  { method: 'Bank Transfer', amount: 85600 }
];

const riskTrendData = [
  { month: 'Jan', percentage: 2.1 },
  { month: 'Feb', percentage: 1.8 },
  { month: 'Mar', percentage: 2.3 },
  { month: 'Apr', percentage: 1.9 },
  { month: 'May', percentage: 2.0 },
  { month: 'Jun', percentage: 1.7 }
];

const VisualTab = ({ isDark }) => {
  return (
    <div>
      {/* Growth Trends */}
      <div className={`rounded-lg shadow-sm border p-6 mb-8 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center mb-6">
          <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Growth Trends (6 Months)
          </h3>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={growthData}>
            <defs>
              <linearGradient id="colorClients" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorGroups" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
            <XAxis dataKey="month" stroke={isDark ? '#9ca3af' : '#6b7280'} />
            <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: isDark ? '#2b323c' : '#fff',
                border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                color: isDark ? '#fff' : '#000'
              }}
            />
            <Area
              type="monotone"
              dataKey="totalClients"
              stroke="#3b82f6"
              fillOpacity={1}
              fill="url(#colorClients)"
              name="Total Clients"
            />
            <Area
              type="monotone"
              dataKey="totalGroups"
              stroke="#10b981"
              fillOpacity={1}
              fill="url(#colorGroups)"
              name="Total Groups"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className={`rounded-lg shadow-sm border p-6 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center mb-6">
            <BarChart3 className="w-5 h-5 text-green-600 mr-2" />
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Loan Portfolio Distribution
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={loanDistributionData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {loanDistributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDark ? '#2b323c' : '#fff',
                  border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                  color: isDark ? '#fff' : '#000'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-4">
            {loanDistributionData.map((entry, index) => (
              <div key={index} className="flex items-center">
                <div className="w-3 h-3 mr-2 rounded" style={{ backgroundColor: entry.color }}></div>
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{entry.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-6 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center mb-6">
            <DollarSign className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Collection Methods
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={collectionMethodsData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="method" stroke={isDark ? '#9ca3af' : '#6b7280'} />
              <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} />
              <Tooltip 
                formatter={(value) => `KSh ${value.toLocaleString()}`}
                contentStyle={{ 
                  backgroundColor: isDark ? '#2b323c' : '#fff',
                  border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                  color: isDark ? '#fff' : '#000'
                }}
              />
              <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Risk Distribution */}
      <div className={`rounded-lg shadow-sm border p-6 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center mb-6 text-red-600">
          <TrendingDown className="w-5 h-5 mr-2" />
          <h3 className={`text-lg font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
            Risk Distribution Trends
          </h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={riskTrendData}>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
            <XAxis dataKey="month" stroke={isDark ? '#9ca3af' : '#6b7280'} />
            <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} />
            <Tooltip 
              formatter={(value) => `${value}%`}
              contentStyle={{ 
                backgroundColor: isDark ? '#2b323c' : '#fff',
                border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                color: isDark ? '#fff' : '#000'
              }}
            />
            <Line
              type="monotone"
              dataKey="percentage"
              stroke="#10b981"
              strokeWidth={3}
              dot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            <strong>Mar:</strong> Percentage: 86% | Percentage: 8% | Percentage: 3%
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualTab;